import { useEffect, useMemo, useState } from "react";
import { Settings as SettingsIcon, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button, SegmentedTabs } from "@akuna/ui";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Switch } from "@akuna/ui";
import { PrescriptionTemplatesTab } from "@/components/prescriptions/PrescriptionTemplatesTab";
import { ExamPrescriptionTemplatesTab } from "@/components/prescriptions/ExamPrescriptionTemplatesTab";
import { ManipulatedPrescriptionTemplatesTab } from "@/components/prescriptions/ManipulatedPrescriptionTemplatesTab";
import { ClinicAppointmentStatusSettings } from "@/components/settings/ClinicAppointmentStatusSettings";
import { ClinicConditionCatalogSettings } from "@/components/settings/ClinicConditionCatalogSettings";
import { ClinicAppointmentTypesSettings } from "@/components/settings/ClinicAppointmentTypesSettings";
import { WhatsAppMetaSettingsTab } from "@/components/settings/WhatsAppMetaSettingsTab";
import { WhatsAppTemplatesPage } from "@/pages/settings/WhatsAppTemplates";
import { CertificateTab } from "@/components/settings/CertificateTab";
import { TermTemplatesTab } from "@/components/terms/TermTemplatesTab";
import { PrintLayoutEditor } from "@/components/settings/print-layout/PrintLayoutEditor";
import { usePrintSettings } from "@/hooks/usePrintSettings";
import { useClinicDefaultAppointmentDurations, useSetClinicDefaultAppointmentDurations } from "@/hooks/useClinicDefaultAppointmentDurations";
import { useClinicFeatureFlags, useUpdateClinicFeatureFlags } from "@/hooks/useClinicFeatureFlags";
import { BEHAVIOR_LABEL_KEYS } from "@/lib/appointments/availability";
import {
  APPOINTMENT_DURATION_BEHAVIOR_KEYS,
  FALLBACK_APPOINTMENT_DURATION_MINUTES,
  type AppointmentDurationBehaviorKey,
} from "@/lib/appointments/default-duration";
import type { AppointmentTypeBehavior } from "@/hooks/useClinicAppointmentTypes";
import { isStagingSubdomain } from "@/lib/runtime-host";
import { useProfile } from "@/hooks/useProfile";
import { useEntitlements } from "@/hooks/useEntitlements";
import { usePermissionsMap } from "@/hooks/usePermissionsMap";
import { useMobileHeader } from "@/contexts/MobileHeaderContext";

type TemplateKind = "standard" | "manipulated" | "exam";

const TEMPLATE_KIND_ORDER: TemplateKind[] = ["standard", "manipulated", "exam"];
const TEMPLATE_KIND_META_KEYS: Record<TemplateKind, { labelKey: string; descKey: string }> = {
  standard: { labelKey: "templateStandard", descKey: "templateStandardDesc" },
  manipulated: { labelKey: "templateManipulated", descKey: "templateManipulatedDesc" },
  exam: { labelKey: "templateExam", descKey: "templateExamDesc" },
};

type SettingsTab =
  | "general"
  | "print"
  | "transcription"
  | "prescription-templates"
  | "term-templates"
  | "whatsapp-meta"
  | "certificate"
  | "appointment-types";
type PrintTab = "prescription" | "term";

const Settings = () => {
  const { t } = useTranslation("settings");
  const { t: tCalendar } = useTranslation("calendar");
  const navigate = useNavigate();
  useMobileHeader({ title: t("title") }, [t]);
  const { settingsTab, printTab, templateTab } = useParams<{
    settingsTab?: string;
    printTab?: string;
    templateTab?: string;
  }>();
  const { isLoading } = usePrintSettings();
  const { data: profile } = useProfile();
  const { data: clinicFeatureFlags, isLoading: isClinicFeatureFlagsLoading } = useClinicFeatureFlags();
  const { data: clinicDurationDefaults, isLoading: isClinicDurationDefaultsLoading } =
    useClinicDefaultAppointmentDurations();
  const { data: entitlements, isLoading: isEntitlementsLoading } = useEntitlements();
  const { data: permissionsMap, isLoading: isPermissionsLoading } = usePermissionsMap();
  const setClinicDurationDefaults = useSetClinicDefaultAppointmentDurations();
  const [durationDraft, setDurationDraft] = useState<Record<AppointmentDurationBehaviorKey, number> | null>(null);
  const updateClinicFeatureFlags = useUpdateClinicFeatureFlags();
  useEffect(() => {
    if (!clinicDurationDefaults?.clinicId) {
      setDurationDraft(null);
      return;
    }
    const next = {} as Record<AppointmentDurationBehaviorKey, number>;
    for (const key of APPOINTMENT_DURATION_BEHAVIOR_KEYS) {
      next[key] = clinicDurationDefaults.minutesByType[key] ?? FALLBACK_APPOINTMENT_DURATION_MINUTES;
    }
    setDurationDraft(next);
  }, [clinicDurationDefaults]);

  const isStaging = isStagingSubdomain();
  const hasWhatsAppModule = Boolean(entitlements?.activeModules?.includes("whatsapp"));
  const canConfigureWhatsApp =
    Boolean(permissionsMap?.admin_settings?.update) ||
    Boolean(permissionsMap?.manage_clinic?.update) ||
    Boolean(permissionsMap?.whatsapp_config?.update);
  const showWhatsAppBetaTab = hasWhatsAppModule && canConfigureWhatsApp;
  const showGeneralBetaTab = isStaging;
  const showAppointmentTypesBetaTab = isStaging;

  const handleSaveDefaultDurations = async () => {
    if (!durationDraft) return;
    for (const key of APPOINTMENT_DURATION_BEHAVIOR_KEYS) {
      const v = durationDraft[key];
      if (!Number.isFinite(v) || v < 1 || v > 1440 || Math.floor(v) !== v) {
        toast.error(t("generalDefaultDurationsInvalid"));
        return;
      }
    }
    try {
      await setClinicDurationDefaults.mutateAsync(durationDraft);
      toast.success(t("generalDefaultDurationsSaved"));
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message === "clinic_required"
          ? t("generalClinicRequired")
          : error instanceof Error
            ? error.message
            : t("generalDefaultDurationsSaveError");
      toast.error(message);
    }
  };

  const handleAppointmentItemsCardFlagChange = async (checked: boolean) => {
    try {
      await updateClinicFeatureFlags.mutateAsync({ showAppointmentItemsOnCards: checked });
      toast.success(
        checked
          ? t("generalAppointmentItemsOnCardsEnabled")
          : t("generalAppointmentItemsOnCardsDisabled"),
      );
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message === "clinic_required"
          ? t("generalClinicRequired")
          : error instanceof Error && error.message === "clinic_update_no_row"
            ? t("generalClinicUpdateNoRow")
            : error instanceof Error
              ? error.message
              : t("generalSettingsSaveError");

      toast.error(message);
    }
  };


  const activeSettingsTab: SettingsTab = useMemo(() => {
    if (printTab) {
      return "print";
    }

    if (templateTab) {
      return "prescription-templates";
    }

    if (settingsTab === "certificate") {
      return "certificate";
    }

    if (
      (showGeneralBetaTab && settingsTab === "general") ||
      settingsTab === "print" ||
      settingsTab === "transcription" ||
      settingsTab === "prescription-templates" ||
      settingsTab === "term-templates" ||
      (showAppointmentTypesBetaTab && settingsTab === "appointment-types") ||
      (showWhatsAppBetaTab && settingsTab === "whatsapp-meta")
    ) {
      return settingsTab;
    }
    return showGeneralBetaTab ? "general" : "print";
  }, [printTab, settingsTab, showAppointmentTypesBetaTab, showGeneralBetaTab, showWhatsAppBetaTab, templateTab]);

  const activePrintTab: PrintTab =
    printTab === "term" ? "term" : "prescription";
  const activeTemplateTab: TemplateKind =
    templateTab === "manipulated" || templateTab === "exam" ? templateTab : "standard";

  useEffect(() => {
    if (settingsTab || printTab || templateTab) return;
    navigate(showGeneralBetaTab ? "/settings/general" : "/settings/print/prescription", { replace: true });
  }, [navigate, printTab, settingsTab, showGeneralBetaTab, templateTab]);

  if (
    isLoading ||
    isClinicFeatureFlagsLoading ||
    isClinicDurationDefaultsLoading ||
    isEntitlementsLoading ||
    isPermissionsLoading
  ) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-full space-y-4 sm:space-y-6">
      <div className="hidden min-w-0 md:block">
        <h1 className="flex min-w-0 items-center gap-2 text-2xl font-bold sm:text-3xl">
          <SettingsIcon className="h-6 w-6 shrink-0 sm:h-8 sm:w-8" />
          {t("title")}
        </h1>
        <p className="mt-2 text-muted-foreground">{t("subtitle")}</p>
      </div>

      <SegmentedTabs
        value={activeSettingsTab}
        onValueChange={(value) => {
          const nextTab = value as SettingsTab;
          if (nextTab === "print") {
            navigate(`/settings/print/${activePrintTab}`);
            return;
          }
          if (nextTab === "prescription-templates") {
            navigate(`/settings/prescription-templates/${activeTemplateTab}`);
            return;
          }
          navigate(`/settings/${nextTab}`);
        }}
        className="max-w-full"
        variant="outline"
        width="full"
        items={[
          ...(showGeneralBetaTab
            ? [
                {
                  value: "general",
                  label: t("tabGeneral"),
                  content: (
                    <div className="space-y-4 sm:space-y-6">
                      <Card>
                        <CardHeader>
                          <CardTitle>{t("generalTitle")}</CardTitle>
                          <CardDescription>{t("generalDescription")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 sm:space-y-6">
                          <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 sm:p-4">
                            <div className="min-w-0 space-y-1">
                              <Label htmlFor="appointment-items-on-cards-switch" className="text-base font-medium">
                                {t("generalAppointmentItemsOnCardsLabel")}
                              </Label>
                              <p className="text-sm text-muted-foreground">
                                {t("generalAppointmentItemsOnCardsDescription")}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {profile?.current_clinic_id
                                  ? t("generalClinicScoped")
                                  : t("generalClinicRequired")}
                              </p>
                            </div>
                            <Switch
                              id="appointment-items-on-cards-switch"
                              checked={clinicFeatureFlags?.showAppointmentItemsOnCards === true}
                              disabled={!profile?.current_clinic_id || updateClinicFeatureFlags.isPending}
                              onCheckedChange={(checked) => {
                                void handleAppointmentItemsCardFlagChange(checked);
                              }}
                            />
                          </div>

                          <div className="space-y-3 rounded-lg border p-3 sm:space-y-4 sm:p-4">
                            <div className="space-y-1">
                              <p className="text-base font-medium">{t("generalDefaultDurationsTitle")}</p>
                              <p className="text-sm text-muted-foreground">{t("generalDefaultDurationsDescription")}</p>
                              <p className="text-xs text-muted-foreground">
                                {profile?.current_clinic_id ? t("generalClinicScoped") : t("generalClinicRequired")}
                              </p>
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                              {APPOINTMENT_DURATION_BEHAVIOR_KEYS.map((key) => (
                                <div key={key} className="space-y-2">
                                  <Label htmlFor={`default-duration-${key}`}>
                                    {tCalendar(BEHAVIOR_LABEL_KEYS[key as AppointmentTypeBehavior])}
                                  </Label>
                                  <div className="flex min-w-0 items-center gap-2">
                                    <Input
                                      id={`default-duration-${key}`}
                                      type="number"
                                      inputMode="numeric"
                                      min={1}
                                      max={1440}
                                      step={1}
                                      disabled={!profile?.current_clinic_id || !durationDraft || setClinicDurationDefaults.isPending}
                                      value={durationDraft?.[key] ?? ""}
                                      onChange={(event) => {
                                        const raw = event.target.value;
                                        setDurationDraft((prev) => {
                                          if (!prev) return prev;
                                          if (raw === "") {
                                            return { ...prev, [key]: FALLBACK_APPOINTMENT_DURATION_MINUTES };
                                          }
                                          const n = parseInt(raw, 10);
                                          return { ...prev, [key]: Number.isNaN(n) ? prev[key] : n };
                                        });
                                      }}
                                    />
                                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                                      {t("generalDefaultDurationsMinutesLabel")}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <Button
                              type="button"
                              disabled={
                                !profile?.current_clinic_id || !durationDraft || setClinicDurationDefaults.isPending
                              }
                              onClick={() => {
                                void handleSaveDefaultDurations();
                              }}
                            >
                              {setClinicDurationDefaults.isPending ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  {t("saving")}
                                </>
                              ) : (
                                t("generalDefaultDurationsSave")
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>{t("conditionCatalogTitle")}</CardTitle>
                          <CardDescription className="space-y-1">
                            <span className="block">{t("conditionCatalogDescription")}</span>
                            <span className="block text-xs text-muted-foreground">
                              {profile?.current_clinic_id
                                ? t("generalClinicScoped")
                                : t("generalClinicRequired")}
                            </span>
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ClinicConditionCatalogSettings />
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle>{t("appointmentStatusesTitle")}</CardTitle>
                          <CardDescription>{t("appointmentStatusesCardDescription")}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ClinicAppointmentStatusSettings />
                        </CardContent>
                      </Card>
                    </div>
                  ),
                },
              ]
            : []),
          {
            value: "print",
            label: t("tabPrint"),
            content: (
              <div className="space-y-4 sm:space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{t("print")}</CardTitle>
                    <CardDescription>{t("printLayoutsDescription")}</CardDescription>
                  </CardHeader>
                </Card>

                <SegmentedTabs
                  value={activePrintTab}
                  onValueChange={(value) => navigate(`/settings/print/${value}`, { replace: true })}
                  variant="outline"
                  width="full"
                  items={[
                    {
                      value: "prescription",
                      label: t("printPrescriptionTab"),
                      content: (
                        <div className="mt-4 sm:mt-6">
                          <PrintLayoutEditor scope="prescription" />
                        </div>
                      ),
                    },
                    {
                      value: "term",
                      label: t("printTermTab"),
                      content: (
                        <div className="mt-4 sm:mt-6">
                          <PrintLayoutEditor scope="term" />
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            ),
          },
          {
            value: "prescription-templates",
            label: t("tabPrescriptionTemplates"),
            content: (
              <div className="space-y-4 sm:space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{t("prescriptionTemplatesTitle")}</CardTitle>
                    <CardDescription>{t("prescriptionTemplatesDesc")}</CardDescription>
                  </CardHeader>
                </Card>

                <SegmentedTabs
                  value={activeTemplateTab}
                  onValueChange={(value) => navigate(`/settings/prescription-templates/${value}`, { replace: true })}
                  variant="outline"
                  width="full"
                  items={TEMPLATE_KIND_ORDER.map((kind) => {
                    const meta = TEMPLATE_KIND_META_KEYS[kind];
                    const content =
                      kind === "standard" ? (
                        <PrescriptionTemplatesTab />
                      ) : kind === "manipulated" ? (
                        <ManipulatedPrescriptionTemplatesTab />
                      ) : (
                        <ExamPrescriptionTemplatesTab />
                      );
                    return {
                      value: kind,
                      label: t(meta.labelKey),
                      content: <div className="mt-4 sm:mt-6">{content}</div>,
                    };
                  })}
                />
              </div>
            ),
          },
          {
            value: "term-templates",
            label: t("tabTermTemplates"),
            content: <TermTemplatesTab />,
          },
          {
            value: "certificate",
            label: t("tabCertificate"),
            content: <CertificateTab />,
          },
          ...(showWhatsAppBetaTab
            ? [
                {
                  value: "whatsapp-meta",
                  label: t("tabWhatsAppMeta"),
                  content: (
                    <SegmentedTabs
                      defaultValue="config"
                      variant="outline"
                      width="full"
                      items={[
                        {
                          value: "config",
                          label: t("whatsAppMetaConfig"),
                          content: <WhatsAppMetaSettingsTab />,
                        },
                        {
                          value: "templates",
                          label: t("whatsAppMetaTemplates"),
                          content: <WhatsAppTemplatesPage />,
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
          ...(showAppointmentTypesBetaTab
            ? [
                {
                  value: "appointment-types",
                  label: t("appointmentTypesTab"),
                  content: <ClinicAppointmentTypesSettings />,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};

export default Settings;
